import { toIntlLocale } from "@/i18n/to-intl-locale";
import type { ControlListItem, ControlTag } from "@/models/control.model";
import { monetaryFilterFn } from "@/shared/utils/filtering/data-table-monetary";
import { numericFilterFn } from "@/shared/utils/filtering/data-table-numeric";
import { monetaryDescNullsLastSortingFn } from "@/shared/utils/sorting/data-table-monetary-sorting";
import { IconButton } from "@spz/ui/components/button";
import { Checkbox } from "@spz/ui/components/checkbox";
import { EmptyCell } from "@spz/ui/components/table/cells/EmptyCell";
import { LinkCell } from "@spz/ui/components/table/cells/link-cell";
import { NumericCell } from "@spz/ui/components/table/cells/numeric-cell";
import { SegmentedProgressCell } from "@spz/ui/components/table/cells/segmented-progress-cell";
import { TagCell } from "@spz/ui/components/table/cells/tag-cell";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@spz/ui/components/tooltip";
import { Typography } from "@spz/ui/components/typography";
import { cn } from "@spz/ui/lib/utils";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTranslateControlWithParametersV2 } from "./use-translate-control-with-parameters-v2";

export function useControlListColumns() {
  const { t, i18n } = useTranslation();
  const locale = toIntlLocale(i18n.language);
  const navigate = useNavigate();

  return useMemo(() => {
    const columnHelper = createColumnHelper<ControlListItem>();

    return [
      columnHelper.display({
        id: "select",
        header: ({ table }) => {
          const handleToggle = table.getToggleAllRowsSelectedHandler();
          return (
            <div
              className="flex items-center w-full justify-center h-full cursor-pointer"
              onClick={handleToggle}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleToggle(e);
                }
              }}
              aria-label={t("select_all")}
            >
              <Checkbox
                checked={
                  table.getIsSomeRowsSelected()
                    ? "indeterminate"
                    : table.getIsAllRowsSelected()
                }
              />
            </div>
          );
        },
        cell: ({ row }) => {
          const canSelect = row.getCanSelect();
          const handleToggle = row.getToggleSelectedHandler();
          return (
            <div
              className={cn(
                "flex items-center justify-center h-full",
                canSelect && "cursor-pointer",
              )}
              onClick={canSelect ? handleToggle : undefined}
              onKeyDown={
                canSelect
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleToggle(e);
                      }
                    }
                  : undefined
              }
              aria-label={t("select_row")}
            >
              <Checkbox checked={row.getIsSelected()} disabled={!canSelect} />
            </div>
          );
        },
        size: 30,
      }),
      columnHelper.accessor((row) => row.shortId, {
        header: t("id"),
        id: "id",
        enableSorting: true,
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) => {
          const control = row.original;
          return (
            <LinkCell
              value={control.shortId}
              to={`/controls/${control.id}/results`}
              data-testid={`control-id-link-${control.shortId}`}
            />
          );
        },
      }),
      columnHelper.accessor("control", {
        header: t("control"),
        size: 800,
        minSize: 800,
        maxSize: Number.MAX_SAFE_INTEGER,
        enableSorting: false,
        cell: ({ row }) => {
          const control = row.original;
          const translatedControl =
            useTranslateControlWithParametersV2(control);
          return (
            <LinkCell
              value={translatedControl}
              to={`/controls/${control.id}/results`}
              data-testid={`control-name-link-${control.shortId}`}
            />
          );
        },
      }),
      columnHelper.accessor("tags", {
        header: t("tags"),
        enableSorting: false,
        size: 300,
        cell: ({ getValue }) => {
          const tags = getValue<ControlTag[]>();
          const associatedTags = tags.map((tag) => tag.name);
          return <TagCell value={associatedTags} maxVisible={2} />;
        },
        filterFn: (row, columnId, filterValue: string[]) => {
          if (!filterValue || filterValue.length === 0) return true;

          const rowTags = row.getValue<ControlTag[]>(columnId);
          const rowTagIds = rowTags.map((tag) => tag.id);

          return filterValue.every((tagId) => rowTagIds.includes(tagId));
        },
      }),
      columnHelper.accessor("resultCount", {
        header: t("result"),
        enableSorting: true,
        size: 100,
        cell: ({ getValue }) => {
          const resultCount = getValue();
          const format = {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          };
          const className =
            Number(resultCount) === 0 ? "text-green-500 font-semibold" : "";
          return (
            <NumericCell
              value={resultCount}
              format={format}
              className={className}
            />
          );
        },
        filterFn: numericFilterFn,
      }),
      columnHelper.accessor("amount", {
        header: t("stake"),
        enableSorting: true,
        size: 100,
        cell: ({ row, getValue }) => {
          const amount = getValue();
          if (!row.original.amount) {
            return <EmptyCell />;
          }
          return (
            <Typography variant="body">{amount.format(locale)}</Typography>
          );
        },
        filterFn: monetaryFilterFn,
        sortingFn: monetaryDescNullsLastSortingFn,
      }),
      columnHelper.accessor("progress", {
        header: t("progress"),
        enableSorting: false,
        size: 160,
        cell: ({ row }) => {
          if (row.original.resultCount === 0) {
            return <EmptyCell />;
          }
          return <SegmentedProgressCell segments={row.original.progress} />;
        },
      }),
      columnHelper.display({
        id: "actions",
        header: t("actions"),
        size: 80,
        minSize: 80,
        maxSize: 80,
        cell: ({ row }) => (
          <div className="flex justify-center items-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  icon="eye"
                  size="xs"
                  variant="ghost"
                  className="cursor-pointer"
                  onClick={() => {
                    navigate(`/controls/${row.original.id}/results`);
                  }}
                />
              </TooltipTrigger>
              <TooltipContent>{t("view_details")}</TooltipContent>
            </Tooltip>
          </div>
        ),
      }),
    ];
  }, [t, locale, navigate]);
}
